import {
    BoxProps,
    forwardRef,
    Flex,
    Box,
    useBreakpointValue,
    ResponsiveValue,
} from "@chakra-ui/react";
import { useState, useRef, ReactElement, useCallback, useEffect, Children } from "react";
import CarouselNavButton from "./CarouselNavButton";
import CarouselDots from "./CarouselDots";

type CarouselSlideProps = BoxProps;

export type CarouselProps = BoxProps & {
    navSpace?: number | string;
    slidesToShow?: number | number[];
    slidesToScroll?: number | number[];
    gap: number | string | (number | string | null)[];
    dots?: ResponsiveValue<boolean>;
    children: ReactElement<CarouselSlideProps> | ReactElement<CarouselSlideProps>[];
};

const Carousel = forwardRef<CarouselProps, "div">((props, ref) => {
    const { children, navSpace, gap, dots, slidesToShow, slidesToScroll } = props;
    const carouselRef = useRef<HTMLDivElement>(null);
    const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
    const [activeDotIndex, setActiveDotIndex] = useState<number>(0);
    const [startX, setStartX] = useState(0);
    const [isSwiping, setIsSwiping] = useState<boolean>(false);

    const slidesToShowCount = slidesToShow ?? 1;
    const slidesToScrollCount = slidesToScroll ?? 1;

    const slidesToShowValue: number = ConvertToValidValue(slidesToShowCount);
    const slidesToScrollValue: number = ConvertToValidValue(slidesToScrollCount);
    const negativeGap = getNegativeGap(gap);

    const childrenElements = Children.toArray(children);

    useEffect(() => {
        const scrollContainer = carouselRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", handleScroll);
            scrollContainer.addEventListener("mousedown", handleTouchStart);
            scrollContainer.addEventListener("mousemove", handleTouchMove);
            scrollContainer.addEventListener("mouseup", handleTouchEnd);
        }

        const observer = new ResizeObserver(() => handleScroll);

        return () => {
            scrollContainer?.removeEventListener("scroll", handleScroll);
            scrollContainer?.removeEventListener("mousedown", handleTouchStart);
            scrollContainer?.removeEventListener("mousemove", handleTouchMove);
            scrollContainer?.removeEventListener("mouseup", handleTouchEnd);
            observer.disconnect();
        };
    }, [slidesToShowValue, isSwiping]);

    const handleTouchStart = (e: MouseEvent) => {
        e.preventDefault();
        setStartX(e.clientX);
        setIsSwiping(true);
    };

    const handleTouchMove = (e: MouseEvent) => {
        e.preventDefault();
        if (!isSwiping) return;
        let currentX = 0;
        currentX = e.clientX;
        const diff = startX - currentX;
        // You can use this 'diff' value to determine how much the user has swiped

        // Example: Update UI based on swipe distance
        if (diff > 50) {
            // Swiped left
            ScrollToElement(activeDotIndex + 1);
        } else if (diff < -50) {
            // Swiped right
            ScrollToElement(activeDotIndex - 1);
        }
    };

    const handleTouchEnd = () => {
        setIsSwiping(false);
    };

    const handleScroll = useCallback(() => {
        const div = carouselRef.current;
        const scrollPosition = div?.scrollLeft;
        if (scrollPosition) {
            const activeChildIndex = findActiveChildIndex(div);
            setActiveItemIndex(activeChildIndex);
            if (activeChildIndex / slidesToShowValue !== -1) {
                setActiveDotIndex(activeChildIndex / slidesToShowValue);
            }
        }
    }, [carouselRef.current?.scrollLeft, slidesToShowValue]);

    const findActiveChildIndex = (div: HTMLDivElement | null) => {
        if (!div) return 0;
        const children = div.children;
        return Array.from(children).findIndex((child: HTMLElement, index) => {
            if (index % slidesToShowValue === 0) {
                return IsScrolledIntoView(div, child);
            }
            return false;
        });
    };

    const IsScrolledIntoView = (container: HTMLElement, elem: HTMLElement) => {
        const containerLeft = container.scrollLeft - 1;
        const containerRight = containerLeft + container.offsetWidth + 1;
        const elemLeft = elem.offsetLeft;
        const elemRight = elemLeft + elem.offsetWidth;
        return elemRight <= containerRight && elemLeft >= containerLeft;
    };

    const ScrollToElement = (elemIndex: number) => {
        carouselRef.current?.scrollTo({
            left: document.getElementById(
                `${props.id}_carousel_slide_${elemIndex * slidesToShowValue}`
            )?.offsetLeft,
            behavior: "smooth",
        });
        handleTouchEnd();
    };

    const slidesElements = childrenElements.map((element, index) => {
        const snap = index % slidesToShowValue === 0 ? "start" : "unset";
        return (
            <Box
                className={`${props.id}_carousel_slide`}
                key={`${props.id}_carousel_slide_${index}`}
                id={`${props.id}_carousel_slide_${index}`}
                scrollSnapAlign={snap}
                width={getSlideWidth(slidesToShowCount)}
                flex="0 0 auto"
                px={gap}
            >
                {element}
            </Box>
        );
    });

    const GetActiveNav = (navType: string) => {
        if (navType === "next") {
            const activeItemIndexAddition = slidesToShowValue === 0 ? 1 : slidesToShowValue;
            return activeItemIndex + activeItemIndexAddition < childrenElements.length;
        } else {
            return activeItemIndex !== 0;
        }
    };

    const activePrev = GetActiveNav("prev") && (
        <CarouselNavButton
            navType="prev"
            marginSpace={negativeGap}
            navSpace={navSpace}
            onClick={() => {
                ScrollToElement(activeDotIndex - 1);
            }}
        />
    );

    const activeNext = GetActiveNav("next") && (
        <CarouselNavButton
            navType="next"
            marginSpace={negativeGap}
            navSpace={navSpace}
            onClick={() => {
                ScrollToElement(activeDotIndex + 1);
            }}
        />
    );

    return (
        <Flex flexDirection="column" ref={ref}>
            <Box position="relative">
                {activePrev}
                <Flex
                    ref={carouselRef}
                    flexDirection="row"
                    overflowX="scroll"
                    overflowY="hidden"
                    scrollSnapType="x mandatory"
                    p="2px"
                    position="relative"
                    mx={negativeGap}
                    sx={{
                        scrollbarWidth: "none",
                        "::-webkit-scrollbar": {
                            width: 0,
                            height: 0,
                        },
                        "::-webkit-scrollbar-track": {
                            boxShadow: "inset 0 0 0 rgba(0,0,0,0)",
                        },
                        "::-webkit-scrollbar-thumb": {
                            backgroundColor: "transparent",
                            outline: "0px solid transparent",
                        },
                        ...props.sx,
                    }}
                >
                    {slidesElements}
                </Flex>
                {activeNext}
            </Box>
            {dots && (
                <CarouselDots
                    carouselId={props.id}
                    childrenCount={childrenElements.length}
                    slidesToShowValue={slidesToShowValue}
                    slidesToScrollValue={slidesToScrollValue}
                    activeDotIndex={activeDotIndex}
                    scrollToElement={ScrollToElement}
                />
            )}
        </Flex>
    );
});

const ConvertToValidValue = (count: number | number[]) => {
    const arr = Array.isArray(count) ? count : [count];
    return useBreakpointValue(arr.map((value) => Math.floor(value))) ?? 1;
};

const getNegativeGap = (gap: number | string | (number | string | null)[]) => {
    const gapValue = gap ?? 0;
    return Array.isArray(gapValue)
        ? gapValue.map((value) => (value ? `-${value}` : null))
        : `-${gapValue}`;
};

const getSlideWidth = (slidesToShowCount: number | number[]) => {
    return Array.isArray(slidesToShowCount)
        ? slidesToShowCount.map((amount: number) => `calc(100% / ${amount})`)
        : `calc(100% / ${slidesToShowCount})`;
};

export default Carousel;
